import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import cn from 'classnames';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import { debounce, isEqual } from 'lodash';
import { Popover, Transition } from '@headlessui/react';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import Button from './Button';

const BadgeButton = ({ name, onClick, active, children }) => {
  const handleSelect = (e) => {
    e.preventDefault();
    onClick(name);
  };
  return (
    <a
      className={cn(
        'flex-1 text-center px-2.5 py-1 rounded-md text-sm hover:bg-gray-200 transition',
        active
          ? 'bg-gray-900 text-white hover:bg-gray-900'
          : 'bg-gray-100 text-gray-800'
      )}
      href="/"
      onClick={handleSelect}
    >
      {children}
    </a>
  );
};

BadgeButton.propTypes = {
  name: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  active: PropTypes.bool.isRequired,
  children: PropTypes.string.isRequired,
};

const FORMAT_BY_MODE = {
  day: 'MM/DD/YYYY',
  month: 'MMM YYYY',
  year: 'YYYY',
};

const MAP_TYPE = {
  day: 'DAILY',
  month: 'MONTHLY',
  year: 'YEARLY',
};

const Callback = ({ trigger, onChange }) => {
  React.useEffect(() => {
    onChange(trigger);
    // eslint-disable-next-line
  }, [trigger]);
  return null;
};

Callback.propTypes = {
  onChange: PropTypes.func.isRequired,
};

function HistogramRange({
  onChange,
  defaultValue,
  defaultMode,
  switchMode,
  panelClassName,
  minDate,
  maxDate,
}) {
  const [mode, setMode] = React.useState(defaultMode);
  const [date, setDate] = React.useState(new Date(defaultValue));
  const popOverRef = React.useRef();
  const handleSet = (newValue) => {
    // popOverRef.current.click();
    setMode(newValue);
  };
  const isOpenedOnce = React.useRef(false);
  // eslint-disable-next-line
  const fetch = React.useCallback(
    debounce((d, m) => {
      const payload = {};
      payload.type = MAP_TYPE[m];
      payload.from = dayjs(d).startOf(m).format('YYYY-MM-DD');
      payload.to = dayjs(d).endOf(m).format('YYYY-MM-DD');
      payload.year = dayjs(d).format('YYYY');
      onChange((state) => {
        const newState = {
          ...state,
          ...payload,
        };
        return isEqual(state, newState) ? state : newState;
      });
    }, 300),
    []
  );
  const handleCallback = (e) => {
    if (e || !isOpenedOnce.current) {
      isOpenedOnce.current = true;
      return;
    }
    fetch(date, mode);
  };
  const handlePrev = () => {
    const newDate = dayjs(date).subtract(1, mode).toDate();
    fetch(newDate, mode);
    setDate(newDate);
  };
  const handleNext = () => {
    const newDate = dayjs(date).add(1, mode).toDate();
    fetch(newDate, mode);
    setDate(newDate);
  };
  const label = React.useMemo(() => {
    if (
      minDate &&
      (dayjs(date).isSame(minDate, mode) ||
        dayjs(date).isBefore(minDate, 'date'))
    ) {
      return dayjs(minDate).format(FORMAT_BY_MODE[mode]);
    }
    return dayjs(date).format(FORMAT_BY_MODE[mode]);
  }, [mode, date, minDate]);
  const disableLeft = useMemo(() => {
    return (
      dayjs(date).isSame(minDate, mode) || dayjs(date).isBefore(minDate, 'date')
    );
  }, [mode, minDate, date]);
  const disableright = useMemo(() => {
    return (
      dayjs(date).isSame(maxDate, mode) || dayjs(date).isAfter(maxDate, 'date')
    );
  }, [mode, maxDate, date]);
  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Callback trigger={open} onChange={handleCallback} />
          <div className="flex rounded">
            <Button
              icon={HiChevronLeft}
              size="sm"
              onClick={handlePrev}
              disabled={disableLeft}
            />
            <Popover.Button
              as={Button}
              ref={popOverRef}
              size="sm"
              className="rounded-none font-bold w-24"
            >
              {label}
            </Popover.Button>
            <Button
              icon={HiChevronRight}
              size="sm"
              onClick={handleNext}
              disabled={disableright}
            />
          </div>
          <Transition
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Popover.Panel className={cn('absolute z-50 w-80', panelClassName)}>
              <div className="p-1 pt-3 bg-white rounded-b-lg border shadow-primary-lg mt-2 border-t-2 border-l-0 border-r-0 border-b-0 border-gray-300">
                {switchMode && (
                  <div className="space-x-2 mb-3 bg-gray-100 w-auto flex justify-center rounded-md p-2">
                    <BadgeButton
                      name="day"
                      onClick={handleSet}
                      active={mode === 'day'}
                    >
                      Day
                    </BadgeButton>
                    <BadgeButton
                      name="month"
                      onClick={handleSet}
                      active={mode === 'month'}
                    >
                      Month
                    </BadgeButton>
                    <BadgeButton
                      name="year"
                      onClick={handleSet}
                      active={mode === 'year'}
                    >
                      Year
                    </BadgeButton>
                  </div>
                )}
                <div>
                  <DatePicker
                    selected={date}
                    onChange={setDate}
                    inline
                    showMonthYearPicker={mode === 'month'}
                    showYearPicker={mode === 'year'}
                    disabledKeyboardNavigation
                    minDate={new Date(minDate)}
                    maxDate={new Date(maxDate)}
                  />
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

HistogramRange.defaultProps = {
  defaultMode: 'year',
  panelClassName: '',
  switchMode: true,
  minDate: null,
  maxDate: null,
};

HistogramRange.propTypes = {
  switchMode: PropTypes.bool,
  defaultMode: PropTypes.string,
  panelClassName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  defaultValue: PropTypes.string.isRequired,
  maxDate: PropTypes.string,
  minDate: PropTypes.string,
};

export default HistogramRange;
